import { Spinner } from "components/DesignSystem/Loader/loader";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "components/DesignSystem/ToggleGroup/ToggleGroup";
import { CashBalanceIcon } from "components/icons/CashBalanceIcon";
import { ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import { formatAmount } from "utils/foramtAmount";

export const MetricItem = ({
  label,
  amount,
}: {
  label: string;
  amount: number;
}) => (
  <div className="t-flex t-flex-col t-gap-1">
    <span className="t-text-body-sm t-text-text-30">{label}</span>
    <span className="t-text-body-sm t-font-medium t-text-text-60">
      {formatAmount(amount)}
    </span>
  </div>
);

export const MetricsDivider = () => (
  <div className="t-h-6 t-border-l t-border-r-0 t-border-solid t-border-neutral-10" />
);

export type TableRowData = {
  uuid: string;
  label: string;
  amount: number;
  link: string;
};

export const DataTable = ({
  columnHeader,
  rows,
}: {
  columnHeader: string;
  rows: TableRowData[];
}) => (
  <div className="t-flex t-flex-col">
    <div className="t-flex t-justify-between t-py-2">
      <span className="t-text-body-sm-lite t-text-text-30">{columnHeader}</span>
      <span className="t-text-body-sm-lite t-text-text-30">Amount</span>
    </div>
    {rows.map((row) => (
      <div
        key={row.uuid}
        className="t-flex t-justify-between t-items-center t-py-1.5"
      >
        <span className="t-text-body-sm t-text-text-60">{row.label}</span>
        <Link
          to={row.link}
          className="t-text-body-sm t-text-text-60 t-no-underline hover:t-underline hover:t-text-purple"
        >
          {formatAmount(row.amount)}
        </Link>
      </div>
    ))}
  </div>
);

export const AccountsCardHeader = ({ title }: { title: string }) => (
  <div className="t-pb-4 t-border-b t-border-solid t-border-neutral-10 t-border-x-0 t-border-t-0">
    <span className="t-text-subtitle-lg t-text-text-black">{title}</span>
  </div>
);

export const AccountsCardLoader = ({ text }: { text: string }) => (
  <div className="t-flex t-flex-col t-justify-center t-items-center t-h-64 t-gap-3">
    <Spinner />
    <span className="t-text-body-sm t-text-text-30">{text}</span>
  </div>
);

export const AccountsCardEmpty = ({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}) => (
  <div className="t-flex t-justify-center t-items-center t-min-h-72 t-text-text-30 t-flex-col sm:t-text-center sm:t-px-4">
    <CashBalanceIcon />
    <span className="t-pt-4 t-text-body-sm">{title}</span>
    <span className="t-text-caption t-mt-2">{subtitle}</span>
  </div>
);

export const AccountsCardError = () => (
  <div className="t-flex t-justify-center t-items-center t-h-64 t-text-red-50">
    Something went wrong
  </div>
);

export const MetricsRow = ({
  totalOutstanding,
  currentAmount,
  overdueAmount,
}: {
  totalOutstanding: number;
  currentAmount: number;
  overdueAmount: number;
}) => (
  <div className="t-flex t-gap-6 t-justify-between t-items-center t-pb-5 t-border-b t-border-solid t-border-neutral-10 t-border-x-0 t-border-t-0">
    <div className="t-flex t-gap-6 t-justify-between t-items-center t-w-full">
      <MetricItem label="Total Outstanding" amount={totalOutstanding} />
      <MetricsDivider />
    </div>
    <div className="t-flex t-gap-6 t-justify-between t-items-center t-w-full">
      <MetricItem label="Current" amount={currentAmount} />
      <MetricsDivider />
    </div>
    <div className="t-w-full">
      <MetricItem label="Overdue" amount={overdueAmount} />
    </div>
  </div>
);

type TabConfig = {
  value: string;
  label: string;
  columnHeader: string;
  rows: TableRowData[];
  viewAllUrl: string;
  viewAllText: string;
};

export const AccountsCardContent = ({
  tabs,
  defaultTab,
  totalOutstanding,
  forcedTab,
  isExport = false,
}: {
  tabs: [TabConfig, TabConfig];
  defaultTab: string;
  totalOutstanding: number;
  forcedTab?: string;
  isExport?: boolean;
}) => {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const resolvedTab = forcedTab ?? activeTab;
  const currentTab = tabs.find((t) => t.value === resolvedTab) || tabs[0];

  return (
    <div className="t-flex t-flex-col t-flex-1">
      <div className="t-flex t-justify-between t-items-center t-pb-5">
        <MetricItem label="Total Outstanding" amount={totalOutstanding} />
        {forcedTab ? (
          <span className="t-text-body-sm t-text-text-60 t-font-medium t-capitalize">
            View by: {currentTab.label}
          </span>
        ) : (
          <ToggleGroup value={activeTab} onValueChange={setActiveTab}>
            {tabs.map((tab) => (
              <ToggleGroupItem key={tab.value} value={tab.value}>
                {tab.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        )}
      </div>

      <DataTable
        columnHeader={currentTab.columnHeader}
        rows={currentTab.rows}
      />

      {!isExport && (
        <Link to={currentTab.viewAllUrl} className="t-mt-auto">
          <Button size="xsmall" customType="link">
            <span>{currentTab.viewAllText}</span>
          </Button>
        </Link>
      )}
    </div>
  );
};

export const AccountsCard = ({
  title,
  loadingText,
  emptyTitle,
  emptySubtitle,
  isLoading,
  isSuccess,
  isError,
  isEmpty,
  children,
}: {
  title: string;
  loadingText: string;
  emptyTitle: string;
  emptySubtitle: string;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  isEmpty: boolean;
  children: ReactNode;
}) => (
  <div className="t-flex t-flex-col t-gap-4 t-bg-surface t-border t-border-solid t-border-neutral-10 t-rounded-lg t-p-5 sm:t-px-3 t-h-full">
    <AccountsCardHeader title={title} />
    <Async.Root
      isLoading={isLoading}
      isSuccess={isSuccess}
      isEmpty={isEmpty}
      isError={isError}
      customLoader={<AccountsCardLoader text={loadingText} />}
    >
      <Async.Empty>
        <AccountsCardEmpty title={emptyTitle} subtitle={emptySubtitle} />
      </Async.Empty>
      <Async.ErrorHandler>
        <AccountsCardError />
      </Async.ErrorHandler>
      <Async.Success>
        <div className="t-flex t-flex-col t-gap-5 t-flex-1">{children}</div>
      </Async.Success>
    </Async.Root>
  </div>
);
